import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { z } from "zod";
import { PageContainer } from "@/layouts/PageContainer";
import { FormField } from "@/components/ui/FormField";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { LoadingState } from "@/components/feedback/LoadingState";
import { ErrorState } from "@/components/feedback/ErrorState";
import {
  useCreatePricingRule,
  usePricingRules,
  useUpdatePricingRule,
} from "@/features/pricing/usePricing";
import { toast } from "@/store/toast.store";
import { cn } from "@/utils/cn";
import type { SurgeWindow } from "@/types/domain";

const schema = z.object({
  baseFare: z.coerce.number().min(0, "Base fare must be ₹0 or more"),
  baseKm: z.coerce.number().min(1, "Base distance must be at least 1 km"),
  stdPerKm: z.coerce.number().positive("Standard rate must be greater than 0"),
  waitingFreeMinutes: z.coerce
    .number()
    .int("Use whole minutes")
    .min(0, "Free waiting can't be negative")
    .max(60, "At most 60 minutes"),
  waitingPerMinute: z.coerce.number().min(0, "Waiting charge can't be negative").max(100, "At most ₹100/min"),
});

type FormValues = z.infer<typeof schema>;

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const KEY_PATTERN = /^[a-z0-9][a-z0-9-]{1,31}$/;

/** Returns a message when a window would be rejected by the API or silently ignored. */
function windowError(w: SurgeWindow): string | null {
  if (!w.label.trim()) return "Give the window a name";
  if (!(w.multiplier >= 1 && w.multiplier <= 3)) return "Multiplier must be between 1 and 3";
  const hasStart = w.startHour !== undefined;
  const hasEnd = w.endHour !== undefined;
  if (hasStart !== hasEnd) return "Set both start and end hour, or leave both empty";
  return null;
}

/** Strip empty optional hours so the API receives only what is set. */
function cleanWindow(w: SurgeWindow): SurgeWindow {
  const { startHour, endHour, ...rest } = w;
  return {
    ...rest,
    label: w.label.trim(),
    ...(startHour !== undefined && endHour !== undefined ? { startHour, endHour } : {}),
  };
}

function describeWindow(w: SurgeWindow): string {
  const days = !w.days?.length || w.days.length === 7 ? "every day" : w.days.map((d) => DAY_LABELS[d]).join(" ");
  const hours =
    w.startHour !== undefined && w.endHour !== undefined ? `${w.startHour}:00–${w.endHour}:00 IST` : "all day";
  return `${days} · ${hours}`;
}

export default function Fares() {
  const rules = usePricingRules();
  const updateRule = useUpdatePricingRule();
  const createRule = useCreatePricingRule();
  const [ruleKey, setRuleKey] = useState("default");
  const [surgeWindows, setSurgeWindows] = useState<SurgeWindow[]>([]);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState("");

  const rule = useMemo(
    () => rules.data?.find((r) => r.key === ruleKey) ?? rules.data?.find((r) => r.key === "default") ?? rules.data?.[0],
    [rules.data, ruleKey],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (rule) {
      reset({
        baseFare: rule.baseFare,
        baseKm: rule.baseKm ?? 5,
        stdPerKm: rule.stdPerKm,
        waitingFreeMinutes: rule.waitingFreeMinutes ?? 3,
        waitingPerMinute: rule.waitingPerMinute ?? 1,
      });
      setSurgeWindows(rule.surgeWindows ?? []);
    }
  }, [rule, reset]);

  if (rules.isLoading) return <LoadingState label="Loading fare configuration…" />;
  if (rules.error) return <ErrorState error={rules.error} onRetry={() => rules.refetch()} />;
  if (!rule) return null;

  const windowsChanged = JSON.stringify(surgeWindows) !== JSON.stringify(rule.surgeWindows ?? []);
  const windowProblem = surgeWindows.map(windowError).find(Boolean) ?? null;
  const keyTaken = rules.data?.some((r) => r.key === newKey);
  const keyProblem = newKey && !KEY_PATTERN.test(newKey)
    ? "Use 2–32 lowercase letters, numbers or dashes"
    : keyTaken
      ? "A rule with this key already exists"
      : null;

  const patchWindow = (i: number, patch: Partial<SurgeWindow>) =>
    setSurgeWindows((prev) => prev.map((w, idx) => (idx === i ? { ...w, ...patch } : w)));

  const parseHour = (v: string): number | undefined => {
    if (v === "") return undefined;
    const n = Math.trunc(Number(v));
    return Number.isFinite(n) ? Math.min(24, Math.max(0, n)) : undefined;
  };

  return (
    <PageContainer
      title="Fare configuration"
      description="Configure a pricing rule: base fare, threshold distance, standard per-km rate, and surge windows. Assign rules to areas from Service zones."
      actions={
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setNewKey("");
            setCreating(true);
          }}
        >
          <Plus className="h-4 w-4" /> New rule
        </Button>
      }
    >
      <form
        className="card flex max-w-2xl flex-col gap-5 p-6"
        onSubmit={handleSubmit((values) =>
          updateRule.mutate(
            { key: rule.key, payload: { ...values, surgeWindows: surgeWindows.map(cleanWindow) } },
            { onSuccess: () => toast.success("Fare configuration updated successfully") },
          ),
        )}
      >
        <FormField
          label="Pricing rule"
          hint={
            rule.key === "default"
              ? "Applies to pickups outside every service zone"
              : "Applies to pickups inside zones assigned to this rule"
          }
        >
          <Select value={rule.key} onChange={(e) => setRuleKey(e.target.value)}>
            {rules.data?.map((r) => (
              <option key={r.key} value={r.key}>
                {r.name ?? r.key}
              </option>
            ))}
          </Select>
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormField
            label="Base fare (₹)"
            error={errors.baseFare?.message}
            hint="Flat fare for trips up to base distance"
          >
            <Input type="number" step="1" {...register("baseFare")} />
          </FormField>

          <FormField
            label="Base distance (km)"
            error={errors.baseKm?.message}
            hint="Distance covered by base fare (default: 5 km)"
          >
            <Input type="number" step="0.5" {...register("baseKm")} />
          </FormField>

          <FormField
            label="Standard ₹/km"
            error={errors.stdPerKm?.message}
            hint="Rate charged only for distance beyond base km"
          >
            <Input type="number" step="0.5" {...register("stdPerKm")} />
          </FormField>
        </div>

        <div className="border-t border-border pt-4">
          <h4 className="mb-1 text-sm font-semibold text-ink">Waiting charge</h4>
          <p className="mb-3 text-xs text-ink-faint">
            After the driver taps "Arrived" the rider waits free for the set minutes; every started minute
            after that is added to the fare. Applies to rides booked under this rule.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Free waiting (minutes)"
              error={errors.waitingFreeMinutes?.message}
              hint="No charge during this window"
            >
              <Input type="number" step="1" min={0} {...register("waitingFreeMinutes")} />
            </FormField>

            <FormField
              label="Waiting ₹/minute"
              error={errors.waitingPerMinute?.message}
              hint="Per started minute after the free window"
            >
              <Input type="number" step="0.5" min={0} {...register("waitingPerMinute")} />
            </FormField>
          </div>
        </div>

        <div className="border-t border-border pt-4">
          <div className="mb-1 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-ink">Surge windows</h4>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                setSurgeWindows((prev) => [
                  ...prev,
                  { label: "", multiplier: 1.2, enabled: true, days: [], startHour: 18, endHour: 21 },
                ])
              }
            >
              <Plus className="h-3.5 w-3.5" /> Add window
            </Button>
          </div>
          <p className="mb-3 text-xs text-ink-faint">
            Multiplies the whole fare while a window is active (IST). Windows are not added together —
            if several match, the first one in the list applies. Leave hours empty and days empty for a
            manual all-day switch. A zone whose rule has no windows never surges.
          </p>
          {surgeWindows.length === 0 && (
            <p className="text-xs text-ink-faint">No surge windows configured.</p>
          )}
          <ul className="flex flex-col gap-2">
            {surgeWindows.map((w, i) => {
              const problem = windowError(w);
              return (
                <li key={i} className="flex flex-col gap-3 rounded-lg border border-border px-3 py-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Input
                      className="h-8 flex-1 text-xs"
                      placeholder="e.g. Mon–Fri 8–10a"
                      value={w.label}
                      onChange={(e) => patchWindow(i, { label: e.target.value })}
                    />
                    <Input
                      className="h-8 w-20 text-xs"
                      type="number"
                      step="0.1"
                      min={1}
                      max={3}
                      aria-label="Multiplier"
                      value={Number.isFinite(w.multiplier) ? w.multiplier : ""}
                      onChange={(e) => patchWindow(i, { multiplier: Number(e.target.value) })}
                    />
                    <span className="text-xs text-ink-muted">x</span>
                    <button
                      type="button"
                      onClick={() => patchWindow(i, { enabled: !w.enabled })}
                      className={w.enabled ? "pill bg-teal-500 text-cream-50" : "pill border border-border text-ink-muted"}
                    >
                      {w.enabled ? "ON" : "off"}
                    </button>
                    <button
                      type="button"
                      aria-label="Remove window"
                      className="text-ink-muted hover:text-red-600"
                      onClick={() => setSurgeWindows((prev) => prev.filter((_, idx) => idx !== i))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="flex items-center gap-1">
                      {DAY_LABELS.map((d, day) => {
                        const on = w.days?.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() =>
                              patchWindow(i, {
                                days: on
                                  ? w.days.filter((x) => x !== day)
                                  : [...(w.days ?? []), day].sort((a, b) => a - b),
                              })
                            }
                            className={cn(
                              "h-7 w-7 rounded-full border text-xs",
                              on ? "border-teal-500 bg-teal-500 text-cream-50" : "border-border text-ink-muted",
                            )}
                          >
                            {d}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-ink-muted">
                      <Input
                        className="h-8 w-16 text-xs"
                        type="number"
                        min={0}
                        max={24}
                        placeholder="from"
                        aria-label="Start hour"
                        value={w.startHour ?? ""}
                        onChange={(e) => patchWindow(i, { startHour: parseHour(e.target.value) })}
                      />
                      to
                      <Input
                        className="h-8 w-16 text-xs"
                        type="number"
                        min={0}
                        max={24}
                        placeholder="to"
                        aria-label="End hour"
                        value={w.endHour ?? ""}
                        onChange={(e) => patchWindow(i, { endHour: parseHour(e.target.value) })}
                      />
                      hour (0–24, end excluded)
                    </div>
                  </div>

                  {problem ? (
                    <p className="text-xs text-red-600">{problem}</p>
                  ) : (
                    <p className="text-xs text-ink-faint">{describeWindow(w)}</p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex justify-end border-t border-border pt-4">
          <Button
            type="submit"
            variant="primary"
            loading={updateRule.isPending}
            disabled={(!isDirty && !windowsChanged) || !!windowProblem}
          >
            Save changes
          </Button>
        </div>
      </form>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New pricing rule"
        footer={
          <>
            <Button variant="outline" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={createRule.isPending}
              disabled={!KEY_PATTERN.test(newKey) || !!keyTaken}
              onClick={() =>
                createRule.mutate(
                  {
                    key: newKey,
                    payload: {
                      baseFare: rule.baseFare,
                      baseKm: rule.baseKm,
                      stdPerKm: rule.stdPerKm,
                      waitingFreeMinutes: rule.waitingFreeMinutes,
                      waitingPerMinute: rule.waitingPerMinute,
                      surgeWindows: [],
                    },
                  },
                  {
                    onSuccess: () => {
                      toast.success("Rule created — assign it to a zone from Service zones");
                      setRuleKey(newKey);
                      setCreating(false);
                    },
                  },
                )
              }
            >
              Create
            </Button>
          </>
        }
      >
        <FormField
          label="Rule key"
          error={keyProblem ?? undefined}
          hint={`Starts with the fares of "${rule.key}" and no surge windows. Edit it after creating.`}
        >
          <Input
            placeholder="e.g. rural"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value.toLowerCase())}
          />
        </FormField>
      </Modal>
    </PageContainer>
  );
}
